<script>

import {computed, onMounted, ref} from "vue";
import useValidation from "@/use/Validation.js";
import useSharedData from "@/use/SharedData.js";
import useSharedSelectData from "@/use/SharedSelectData.js";
import useSharedMethod from "@/use/SharedMethod.js";
import {useDataSelectStore} from "@/stores/DataSelect.js";
import useTools from "@/use/Tools.js";
import StrDataSelectTypeSelect from "@/views/DataSelect/Components/StrDataSelectTypeSelect.vue";
import axios from "axios";
import WCodeGeneration from "@/components/WCodeGeneration.vue";

export default {
    components: {WCodeGeneration, StrDataSelectTypeSelect},

    props: {
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
        externType: String
    },
    setup(props) {
        const {mxRules} = useValidation();

        const {dialog, loading, overlay} = useSharedData();

        const {dataSelectTypes} = useSharedSelectData();

        const {getBack} = useSharedMethod();

        const {$refs, $route} = useTools();

        const readonly = computed(() => props.deleteAction || props.viewAction);

        const dataSelectStore = useDataSelectStore();

        const dataSelect = ref({
            primary: false,
            type: props.externType
        });

        function findById(id) {
            loading.value = true;
            axios.get(`/dataSelects/${id}`).then((response) => {
                dataSelect.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }

        function save(addNew) {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/dataSelects', dataSelect.value).then((response) => {
                    dataSelectStore.addDataSelect(response.data);
                    if (addNew) {
                        dataSelect.value.name = null;
                        dataSelect.value.arabicName = null;
                        dataSelect.value.primary = false;
                    } else {
                        getBack();
                    }
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/dataSelects', dataSelect.value).then((response) => {
                    dataSelectStore.updateDataSelect(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.delete(`/dataSelects/${dataSelect.value.id}`).then(() => {
                    dataSelectStore.deleteDataSelect(dataSelect.value);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        onMounted(() => {
            if (!props.addAction) {
                findById($route.params.id);
            }
        });

        return {
            dataSelectStore,
            mxRules,
            dialog,
            loading,
            overlay,
            readonly,
            dataSelectTypes,
            dataSelect,
            save,
            update,
            remove,
            getBack,
        };
    },
};
</script>

<template>
    <v-dialog
        v-model="dialog"
        max-width="800"
        persistent
        scrollable
    >
        <v-card>
            <w-dialog-toolbar
                :view-action="viewAction"
                :delete-action="deleteAction"
                :add-action="addAction"
                :update-action="updateAction"
            ></w-dialog-toolbar>
            <w-overlay
                :overlay="overlay"
                :loading="loading"
                :delete-action="deleteAction"
                @remove="remove"
                @cancel="overlay = false"
            ></w-overlay>
            <v-card-text>
                <v-form ref="form" lazy-validation>
                    <v-container fluid>
                        <w-fieldset :title="$t('common.details')">
                            <v-row>
                                <v-col cols="12">
                                    <w-label :label="$t('common.code')" required/>
                                    <w-code-generation
                                        v-model="dataSelect.code"
                                        entity="DATA_SELECT"
                                        :load="addAction"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('common.label')" required/>
                                    <w-text-field
                                        v-model="dataSelect.name"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <!--<v-col cols="12">
                                    <w-label label="Nom Arabe"/>
                                    <w-text-field
                                        v-model="dataSelect.arabicName"
                                        :readonly="readonly"
                                    />
                                </v-col>-->
                                <v-col cols="12" v-if="!externType">
                                    <w-label :label="$t('data_select.type')" required/>
                                    <StrDataSelectTypeSelect
                                        v-model="dataSelect.type"
                                        open-all
                                        is-disabled="isDisabled"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                    </v-container>
                </v-form>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save(false)"
                />
                <w-btn
                    v-if="addAction"
                    color="cyan"
                    icon="mdi-plus-circle-outline"
                    :text="$t('common.add_and_new')"
                    :loading="loading"
                    @press="save(true)"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    :loading="loading"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>